import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { VoucherForm } from '../../components/voucher/VoucherForm';
import { toast } from 'sonner';

export const EditVoucherPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Check if this came from a duplication
  const fromDuplication = location.state?.fromDuplication || false;

  // Determine base path based on current route (for accounts vs employee)
  const isAccountsPath = location.pathname.startsWith('/accounts');
  const basePath = isAccountsPath ? '/accounts/vouchers' : '/vouchers';

  useEffect(() => {
    const fetchVoucher = async () => {
      try {
        const data = await vouchersApi.getById(id);
        if (data.status !== 'DRAFT') {
          toast.error('Can only edit vouchers in draft status');
          navigate(`${basePath}/${id}`);
          return;
        }
        setVoucher(data);
      } catch (error) {
        toast.error('Voucher not found');
        navigate(basePath);
      } finally {
        setLoading(false);
      }
    };

    fetchVoucher();
  }, [id, navigate, basePath]);

  const handleSubmit = async (data) => {
    setSaving(true);
    try {
      await vouchersApi.update(id, data);
      toast.success('Voucher updated successfully');
      navigate(`${basePath}/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update voucher');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    // If this was from a duplication and user cancels, delete the duplicated voucher
    if (fromDuplication) {
      try {
        await vouchersApi.delete(id);
        toast.info('Duplicated voucher discarded');
        navigate(basePath);
      } catch (error) {
        console.error('Failed to delete duplicated voucher:', error);
        navigate(basePath);
      }
    } else {
      // Normal cancel - just go back to voucher detail
      navigate(`${basePath}/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // This is always an edit page - voucher exists with an ID
  // New vouchers are created via NewVoucherPage, not here
  const isEditing = !fromDuplication; // Show "Create Voucher" for duplicates, "Update Voucher" for edits

  return (
    <div className="space-y-6" data-testid="edit-voucher-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight">
          {fromDuplication ? 'New Voucher (Duplicated)' : 'Edit Voucher'}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {fromDuplication 
            ? 'Review and save the duplicated voucher details'
            : 'Update voucher details and add attachments'}
        </p>
      </div>

      <VoucherForm
        voucher={voucher}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={saving}
        isEditing={isEditing}
      />
    </div>
  );
};

export default EditVoucherPage;
